import type { ClientRow, DraftOrder, DeliverySpotRow, PickupSpotRow } from "./types";
import type { ImportedFieldBadges } from "./parsed-preview";

export const FIELD_LABELS: Partial<Record<keyof DraftOrder, string>> = {
  order_number: "注文番号",
  payment_number: "注文元ID・決済番号",
  payment_type: "支払区分",
  delivery_date: "配送日",
  delivery_time_start: "配送時刻",
  delivery_time_end: "配送時刻",
  delivery_address: "配送先住所",
  delivery_building: "ビル・施設名",
  handover_location: "引渡し場所",
  arrival_instructions: "到着後の対応",
  delivery_spot_public_id: "納品先マスタ",
  delivery_company: "会社名",
  delivery_branch: "支店・営業所",
  delivery_person_name: "担当者氏名",
  delivery_person_name_kana: "担当者名（カナ）",
  delivery_phone: "連絡先電話番号",
  delivery_email: "メールアドレス",
  client_id: "取引先マスタ",
  pickup_spot_public_id: "集荷先マスタ",
  store_name: "集荷先名",
  store_address: "集荷先住所",
  store_phone: "集荷先電話番号",
  pickup_time_start: "集荷時刻",
  pickup_time_end: "集荷時刻",
  pickup_unavailable_times: "集荷不可時間",
  pickup_duration_minutes: "集荷作業時間",
  delivery_packaging: "納品書荷姿",
  store_message: "店舗様への伝言",
  delivery_notes: "納品先からのご要望",
  day_of_contact_person: "当日連絡先担当者",
  day_of_contact_phone: "当日連絡先電話番号",
  delivery_duration_minutes: "納品作業時間",
  delivery_unavailable_times: "配送不可時間",
  remarks: "備考",
  items: "明細",
};

/**
 * 0 時台の配送時刻を入力したとき、配送日が実際の納品日であることを確認させる (#651)。
 * 日付と時刻だけでは「前日便の続き」かを判別できないため自動補正はせず、誤登録しやすい
 * 0 時台だけ非ブロッキングの注意を表示する。
 */
export function getMidnightDeliveryDateWarning(
  deliveryDate: string,
  deliveryTimeStart: string,
  deliveryTimeEnd: string,
): string | null {
  if (
    !deliveryDate ||
    ![deliveryTimeStart, deliveryTimeEnd].some((time) => time.startsWith("00:"))
  ) {
    return null;
  }
  return `0時台は${deliveryDate}の早朝便です。前日から続く配送でも、実際に納品する日付を配送日に指定してください`;
}

/**
 * 開始だけ入力された配達時刻への注意 (#688)。
 *
 * 注文書に「12:00」とだけ書かれた指定は**納品期限**なので終了側に入れる。開始側に入れると
 * 空いた終了側が便区分窓の右端 (午前便なら 14:00) まで広がり、指定時刻より後ろに到着する
 * 配車が組まれる。2026-08-17 の受注で実際に「11:30 指定 → 到着予定 13:43」が起きた。
 *
 * 終了だけの指定は正しい入れ方なので警告しない (取込時の補完 #657 も終了だけの組を想定している)。
 */
export function getOneSidedDeliveryTimeWarning(
  deliveryTimeStart: string,
  deliveryTimeEnd: string,
): string | null {
  const start = deliveryTimeStart.trim();
  const end = deliveryTimeEnd.trim();
  if (!start || end) return null;
  return `終了が空のままだと便区分の終わりまで枠が広がり、${start}より後に到着する配車になることがあります。単独の指定時刻（納品期限）は終了側に入れてください`;
}

export interface DeliveryCoordinateValidation {
  deliveryCoords: { lat: number; lng: number } | null;
  deliverySpot: DeliverySpotRow | null;
  deliveryNeedsConfirmation: boolean;
}

function isFiniteCoordinate(value: number): boolean {
  return Number.isFinite(value);
}

/**
 * マスタ (集荷先 / 納品先) の緯度・経度文字列を地図表示用の数値座標へ変換する。
 * 未設定・空文字・非数値は null を返す。
 */
export function parseMasterCoords(
  spot: { latitude?: string | null; longitude?: string | null } | null | undefined,
): { lat: number; lng: number } | null {
  const latitude = spot?.latitude?.trim();
  const longitude = spot?.longitude?.trim();
  if (!latitude || !longitude) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!isFiniteCoordinate(lat) || !isFiniteCoordinate(lng)) return null;
  return { lat, lng };
}

/**
 * 地図に表示する座標を「受注固有座標 → 選択中マスタ座標」の優先順で解決する。
 * 表示専用の解決であり、マスタ座標を受注 (order_locations) へ複製しない。
 * 最適化は従来どおり API 側で `pickup_spots` / 納品先マスタの最新座標へフォールバックする。
 */
export function resolveDisplayCoords(
  orderCoords: { lat: number; lng: number } | null,
  spot: { latitude?: string | null; longitude?: string | null } | null | undefined,
): { lat: number; lng: number } | null {
  return orderCoords ?? parseMasterCoords(spot);
}

function hasMasterCoordinates(spot: DeliverySpotRow | null): boolean {
  return parseMasterCoords(spot) !== null;
}

function getDeliveryCoordinateBlockingReason(
  value: DraftOrder,
  validation?: DeliveryCoordinateValidation,
): "配送先座標" | "配送先座標の確認" | null {
  if (!validation || !value.delivery_address.trim()) return null;
  if (value.delivery_spot_public_id) {
    const selectedSpot =
      validation.deliverySpot?.id === value.delivery_spot_public_id
        ? validation.deliverySpot
        : null;
    return hasMasterCoordinates(selectedSpot) ? null : "配送先座標";
  }
  const hasInlineCoordinates =
    validation.deliveryCoords !== null &&
    isFiniteCoordinate(validation.deliveryCoords.lat) &&
    isFiniteCoordinate(validation.deliveryCoords.lng);
  if (!hasInlineCoordinates) return "配送先座標";
  return validation.deliveryNeedsConfirmation ? "配送先座標の確認" : null;
}

export function getOrderBlockingReasons(
  value: DraftOrder,
  deliveryValidation?: DeliveryCoordinateValidation,
): string[] {
  const reasons: string[] = [];
  if (!value.order_number.trim()) reasons.push("注文番号");
  if (!value.delivery_address.trim()) reasons.push("配送先住所");
  const coordinateReason = getDeliveryCoordinateBlockingReason(value, deliveryValidation);
  if (coordinateReason) reasons.push(coordinateReason);
  if (value.delivery_date.length !== 10) reasons.push("配送日");
  if (!value.payment_type.trim()) reasons.push("支払区分");
  if (value.items.length === 0) {
    reasons.push("明細");
  } else {
    const invalidItemIndex = value.items.findIndex(
      (item) => !item.product_name.trim() || item.quantity < 1,
    );
    if (invalidItemIndex >= 0) reasons.push(`明細${invalidItemIndex + 1}行目`);
  }
  return reasons;
}

export function getFirstBlockingFieldKey(
  value: DraftOrder,
  deliveryValidation?: DeliveryCoordinateValidation,
): keyof DraftOrder | null {
  if (!value.order_number.trim()) return "order_number";
  if (!value.delivery_address.trim()) return "delivery_address";
  if (getDeliveryCoordinateBlockingReason(value, deliveryValidation)) return "delivery_address";
  if (value.delivery_date.length !== 10) return "delivery_date";
  if (!value.payment_type) return "payment_type";
  if (value.items.length === 0) return "items";
  const invalidItemIndex = value.items.findIndex(
    (item) => !item.product_name.trim() || item.quantity < 1,
  );
  if (invalidItemIndex >= 0) return "items";
  return null;
}

export function getReviewFieldKeys(badges: ImportedFieldBadges): Array<keyof DraftOrder> {
  return Object.entries(badges)
    .filter(([, badge]) => badge === "要確認")
    .map(([field]) => field as keyof DraftOrder);
}

export function scrollToOrderField(field: keyof DraftOrder): void {
  const target = document.querySelector<HTMLElement>(`[data-order-field="${field}"]`);
  if (!target) return;
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  target.animate(
    [
      { outlineColor: "oklch(0.72 0.12 70)", backgroundColor: "oklch(0.98 0.04 80)" },
      { outlineColor: "transparent", backgroundColor: "transparent" },
    ],
    { duration: 1200, easing: "ease-out" },
  );
}

export interface MasterFieldConflict {
  field: keyof DraftOrder;
  label: string;
  current: string;
  next: string;
}

export interface MasterApplyResult {
  /** 空欄だったため黙ってマスタ値で補完したフィールド */
  filled: Array<keyof DraftOrder>;
  /** 入力済みかつマスタ値と異なるため上書きしなかったフィールド (入力値が正) */
  conflicts: MasterFieldConflict[];
}

type DraftFieldWriter = <TField extends keyof DraftOrder>(
  field: TField,
  value: DraftOrder[TField],
) => void;

// マスタはデフォルト値・注文書 (入力値) が正 (order-input-vs-master-precedence)。
// 空欄だけマスタ値で補完し、差分のある入力済みフィールドは触らず conflict として返す。
// 呼び出し側はインラインで差分を提示し、ユーザーが明示操作したときだけマスタ値を反映する。
function applyMasterFields(
  setFieldValue: DraftFieldWriter,
  fields: Array<{ field: keyof DraftOrder & MasterStringField; current: string; next: string }>,
): MasterApplyResult {
  const result: MasterApplyResult = { filled: [], conflicts: [] };
  for (const { field, current, next } of fields) {
    if (!next.trim()) continue;
    if (!current.trim()) {
      setFieldValue(field, next);
      result.filled.push(field);
    } else if (current.trim() !== next.trim()) {
      result.conflicts.push({ field, label: FIELD_LABELS[field] ?? field, current, next });
    }
  }
  return result;
}

type MasterStringField =
  | "delivery_company"
  | "delivery_address"
  | "delivery_phone"
  | "delivery_email"
  | "delivery_person_name"
  | "delivery_person_name_kana"
  | "delivery_branch"
  | "delivery_building"
  | "handover_location"
  | "store_name"
  | "store_address";

export function applyDeliverySpotMasterToDraft(
  value: DraftOrder,
  deliverySpot: DeliverySpotRow,
  setFieldValue: DraftFieldWriter,
): MasterApplyResult {
  const result = applyMasterFields(setFieldValue, [
    { field: "delivery_company", current: value.delivery_company, next: deliverySpot.name },
    { field: "delivery_address", current: value.delivery_address, next: deliverySpot.address },
    { field: "delivery_phone", current: value.delivery_phone, next: deliverySpot.phone ?? "" },
    { field: "delivery_email", current: value.delivery_email, next: deliverySpot.email ?? "" },
    {
      field: "delivery_person_name",
      current: value.delivery_person_name,
      next: deliverySpot.person_name ?? "",
    },
    {
      field: "delivery_person_name_kana",
      current: value.delivery_person_name_kana,
      next: deliverySpot.person_name_kana ?? "",
    },
    {
      field: "delivery_branch",
      current: value.delivery_branch,
      next: deliverySpot.branch ?? "",
    },
    {
      field: "delivery_building",
      current: value.delivery_building,
      next: deliverySpot.building ?? "",
    },
    {
      field: "handover_location",
      current: value.handover_location,
      next: deliverySpot.handover_location ?? "",
    },
  ]);

  // 作業時間の補完: 注文側が未設定でマスタに値があるときだけ反映する。
  if (value.delivery_duration_minutes === null && deliverySpot.default_duration_minutes !== null) {
    setFieldValue("delivery_duration_minutes", deliverySpot.default_duration_minutes);
    result.filled.push("delivery_duration_minutes");
  }

  // 配送不可時間: 注文側が空でマスタに値があるときだけ補完する。
  if (value.delivery_unavailable_times.length === 0 && deliverySpot.unavailable_times.length > 0) {
    setFieldValue("delivery_unavailable_times", deliverySpot.unavailable_times);
    result.filled.push("delivery_unavailable_times");
  }

  return result;
}

export interface ClientMasterApplyResult extends MasterApplyResult {
  /**
   * 集荷先住所を補完したときだけ返す取引先マスタの座標。
   * 住所を採らない (入力値が正) 場合に座標だけ複製すると住所と食い違うため null にする。
   */
  pickupCoords: { lat: number; lng: number } | null;
}

/**
 * 取引先マスタの既定値を受注ドラフトへ反映する (Issue #613)。
 * 出荷元が固定の取引先 (注文書に集荷住所が載らない) 向けに集荷先住所と座標を補完する。
 * 集荷先マスタと同じく空欄のみ補完し、差分は conflict として呼び出し側へ返す。
 */
export function applyClientMasterToDraft(
  value: DraftOrder,
  client: ClientRow,
  setFieldValue: DraftFieldWriter,
): ClientMasterApplyResult {
  const result = applyMasterFields(setFieldValue, [
    { field: "store_address", current: value.store_address, next: client.pickup_address ?? "" },
  ]);
  const filledAddress = result.filled.includes("store_address");
  return {
    ...result,
    pickupCoords: filledAddress
      ? parseMasterCoords({ latitude: client.pickup_latitude, longitude: client.pickup_longitude })
      : null,
  };
}

export function applyPickupSpotMasterToDraft(
  value: DraftOrder,
  spot: PickupSpotRow,
  setFieldValue: DraftFieldWriter,
): MasterApplyResult {
  const result = applyMasterFields(setFieldValue, [
    { field: "store_name", current: value.store_name, next: spot.spot_name },
    { field: "store_address", current: value.store_address, next: spot.address },
  ]);

  // #614: 標準集荷時刻 (default_pickup_time_from/to) は注文へ複製しない。
  // 複製すると optimizer 側でハードな集荷時間窓として扱われ、目安に過ぎない時刻で
  // 集荷が固定されてしまう (待機の発生源)。さらに注文側に値が入ると集荷可能時刻より
  // 優先されるため、拠点マスタの集荷可能時刻を直しても再最適化に反映されなくなる。
  // 注文の集荷時刻は「注文書に明示された指定」だけを入れ、拠点の営業時間帯は
  // 集荷可能時刻 (available_time_from/to) で表現する。

  // 集荷不可時間: 注文側が空でマスタに値があるときだけ補完する。
  const pickupUnavailableTimes = value.pickup_unavailable_times ?? [];
  const spotUnavailableTimes = spot.unavailable_times ?? [];
  if (pickupUnavailableTimes.length === 0 && spotUnavailableTimes.length > 0) {
    setFieldValue("pickup_unavailable_times", spotUnavailableTimes);
    result.filled.push("pickup_unavailable_times");
  }

  // 集荷作業時間の補完: 注文側が未設定でマスタに値があるときだけ反映する。
  if (value.pickup_duration_minutes === null && spot.default_pickup_duration_minutes !== null) {
    setFieldValue("pickup_duration_minutes", spot.default_pickup_duration_minutes);
    result.filled.push("pickup_duration_minutes");
  }

  return result;
}
